import { Modal, Table, Button, Badge, Alert } from 'react-bootstrap';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { BillingInvoice, BillingDocument, billingService } from '../../services/billing.service';
import { useState } from 'react';

type DetailMode = 'invoices' | 'documents';

interface InvoiceDetailModalProps {
  show: boolean;
  onHide: () => void;
  mode?: DetailMode;
  invoice?: BillingInvoice;
  billingDocument?: BillingDocument;
  isAdmin?: boolean;
  onSubmitApproval?: (document: BillingDocument) => void;
  onApprove?: (document: BillingDocument) => void;
  onReject?: (document: BillingDocument, reason: string) => void;
  onIssue?: (document: BillingDocument) => void;
  onConvert?: (document: BillingDocument) => void;
  onResend?: (document: BillingDocument) => void;
  onReissueRequest?: (document: BillingDocument, reason?: string) => void;
  onReissueApprove?: (document: BillingDocument) => void;
  onReissueReject?: (document: BillingDocument, reason: string) => void;
  onVoid?: (document: BillingDocument) => void;
}

export default function InvoiceDetailModal({
  show,
  onHide,
  mode = 'invoices',
  invoice,
  billingDocument,
  isAdmin = false,
  onSubmitApproval,
  onApprove,
  onReject,
  onIssue,
  onConvert,
  onResend,
  onReissueRequest,
  onReissueApprove,
  onReissueReject,
  onVoid,
}: InvoiceDetailModalProps) {
  const { t, i18n } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  const isDocuments = mode === 'documents';
  const current = isDocuments ? billingDocument : invoice;
  if (!current) {
    return null;
  }

  const getStatusVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'unpaid':
        return 'warning';
      case 'past_due':
        return 'danger';
      case 'void':
        return 'secondary';
      case 'draft':
        return 'secondary';
      case 'pending_approval':
        return 'warning';
      case 'approved':
        return 'info';
      case 'issued':
        return 'success';
      case 'rejected':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      const blob = isDocuments
        ? await billingService.downloadDocumentPdf(current.id)
        : await billingService.downloadInvoicePdf(current.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = isDocuments
        ? `document_${(current as BillingDocument).documentNumber}.pdf`
        : `invoice_${(current as BillingInvoice).invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('PDF download error:', error);
      alert(t('billing.invoice_download_failed'));
    } finally {
      setIsDownloading(false);
    }
  };

  const getReissueDiffs = (document: BillingDocument) => {
    const payload = document.reissuePayload;
    if (!payload) {
      return [t('billing.document_reissue_no_payload', { defaultValue: '変更内容は未入力です' })];
    }
    const diffs: string[] = [];

    if (payload.title !== undefined && payload.title !== document.title) {
      diffs.push(
        `${t('billing.document_title')}: "${document.title || ''}" → "${payload.title || ''}"`
      );
    }
    if (payload.notes !== undefined && payload.notes !== document.notes) {
      diffs.push(
        `${t('billing.document_notes')}: "${document.notes || ''}" → "${payload.notes || ''}"`
      );
    }
    if (payload.dueAt !== undefined && payload.dueAt !== document.dueAt) {
      diffs.push(
        `${t('billing.document_due_at')}: "${document.dueAt || ''}" → "${payload.dueAt || ''}"`
      );
    }
    if (payload.items && payload.items.length > 0) {
      diffs.push(
        `${t('billing.invoice_items')}: ${document.items?.length || 0} → ${payload.items.length}`
      );
    }

    return diffs.length > 0
      ? diffs
      : [t('billing.document_reissue_no_payload', { defaultValue: '変更内容は未入力です' })];
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isDocuments
            ? t('billing.document_detail_title', { defaultValue: '書類詳細' })
            : t('billing.invoice_detail_title')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>
              {isDocuments
                ? t('billing.document_number', { defaultValue: '書類番号' })
                : t('billing.invoice_number')}
              :{' '}
              {isDocuments
                ? (current as BillingDocument).documentNumber
                : (current as BillingInvoice).invoiceNumber}
            </h5>
            <Badge bg={getStatusVariant(current.status)}>{current.status}</Badge>
          </div>

          {isDocuments && (
            <Alert variant="info" className="mb-3">
              <div className="fw-semibold">
                {t('billing.document_action_help_title', { defaultValue: '操作の意味' })}
              </div>
              <div className="small mt-1">
                {t('billing.document_action_help_resend', {
                  defaultValue: '再送: 既存のPDFをそのまま再送します。',
                })}
              </div>
              <div className="small mt-1">
                {t('billing.document_action_help_reissue', {
                  defaultValue: '再発行: 内容を変更して新しい書類を作成します。',
                })}
              </div>
              <div className="small mt-1">
                {t('billing.document_action_help_void', {
                  defaultValue: '無効化: 既発行の書類を無効として扱います。',
                })}
              </div>
            </Alert>
          )}

          <div className="row mb-3">
            <div className="col-md-6">
              <strong>{t('billing.invoice_issued_at')}:</strong>{' '}
              {current.issuedAt
                ? format(
                    new Date(current.issuedAt),
                    i18n.language === 'ja'
                      ? t('common.date_format_long')
                      : t('common.date_format_iso'),
                    { locale: ja }
                  )
                : '-'}
            </div>
            <div className="col-md-6">
              <strong>{t('billing.invoice_due_at')}:</strong>{' '}
              {current.dueAt
                ? format(
                    new Date(current.dueAt),
                    i18n.language === 'ja'
                      ? t('common.date_format_long')
                      : t('common.date_format_iso'),
                    { locale: ja }
                  )
                : '-'}
            </div>
          </div>

          {isDocuments && (current as BillingDocument).reissueStatus && (
            <div className="mb-3">
              <strong>
                {t('billing.document_reissue_status', { defaultValue: '再発行申請' })}:
              </strong>{' '}
              {(current as BillingDocument).reissueStatus}
            </div>
          )}
          {isDocuments && (current as BillingDocument).reissueStatus === 'pending' && (
            <div className="mb-3">
              <strong>{t('billing.document_reissue_diff', { defaultValue: '変更内容' })}:</strong>
              <ul className="mt-2 mb-0">
                {getReissueDiffs(current as BillingDocument).map((item, index) => (
                  <li key={`reissue-diff-${index}`}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {!isDocuments && (current as BillingInvoice).paidAt && (
            <div className="mb-3">
              <strong>{t('billing.invoice_paid_at')}:</strong>{' '}
              {format(
                new Date((current as BillingInvoice).paidAt as string),
                i18n.language === 'ja' ? t('common.date_format_long') : t('common.date_format_iso'),
                { locale: ja }
              )}
            </div>
          )}

          {current.items && current.items.length > 0 && (
            <div className="mb-4">
              <h6>{t('billing.invoice_items')}</h6>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>{t('billing.invoice_item_description')}</th>
                    <th className="text-end">{t('billing.invoice_item_quantity')}</th>
                    <th className="text-end">{t('billing.invoice_item_unit_price')}</th>
                    <th className="text-end">{t('billing.invoice_item_tax')}</th>
                    <th className="text-end">{t('billing.invoice_item_total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {current.items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.description}</td>
                      <td className="text-end">{item.quantity}</td>
                      <td className="text-end">${(item.unitPriceCentsUsd / 100).toFixed(2)}</td>
                      <td className="text-end">${(item.taxCentsUsd / 100).toFixed(2)}</td>
                      <td className="text-end">${(item.totalCentsUsd / 100).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={4} className="text-end">
                      {t('billing.invoice_subtotal')}:
                    </th>
                    <th className="text-end">${(current.subtotalCentsUsd / 100).toFixed(2)}</th>
                  </tr>
                  <tr>
                    <th colSpan={4} className="text-end">
                      {t('billing.invoice_tax_amount')}:
                    </th>
                    <th className="text-end">${(current.taxCentsUsd / 100).toFixed(2)}</th>
                  </tr>
                  <tr>
                    <th colSpan={4} className="text-end">
                      {t('billing.invoice_total_amount')}:
                    </th>
                    <th className="text-end">${(current.totalCentsUsd / 100).toFixed(2)}</th>
                  </tr>
                </tfoot>
              </Table>
            </div>
          )}

          {!current.items || current.items.length === 0 ? (
            <Alert variant="info">{t('billing.invoice_no_items')}</Alert>
          ) : null}
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex flex-column align-items-stretch gap-2">
        {isDocuments && (
          <>
            <div className="text-muted small">
              {t('billing.document_action_section_workflow', {
                defaultValue: '承認・発行フロー',
              })}
            </div>
            <div className="d-flex flex-wrap gap-2">
              {current.status === 'draft' || current.status === 'rejected' ? (
                <Button
                  variant="warning"
                  onClick={() => {
                    if (onSubmitApproval && billingDocument) {
                      onSubmitApproval(billingDocument);
                    }
                  }}
                >
                  {t('billing.document_submit_approval', { defaultValue: '承認申請' })}
                </Button>
              ) : null}
              {current.status === 'pending_approval' && isAdmin ? (
                <>
                  <Button
                    variant="success"
                    onClick={() => {
                      if (onApprove && billingDocument) {
                        onApprove(billingDocument);
                      }
                    }}
                  >
                    {t('billing.document_approve', { defaultValue: '承認' })}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => {
                      if (onReject && billingDocument) {
                        const reason = window.prompt(
                          t('billing.document_reject_reason', {
                            defaultValue: '差し戻し理由を入力してください',
                          })
                        );
                        if (reason) {
                          onReject(billingDocument, reason);
                        }
                      }
                    }}
                  >
                    {t('billing.document_reject', { defaultValue: '差し戻し' })}
                  </Button>
                </>
              ) : null}
              {current.status === 'approved' && isAdmin ? (
                <Button
                  variant="primary"
                  onClick={() => {
                    if (onIssue && billingDocument) {
                      onIssue(billingDocument);
                    }
                  }}
                >
                  {t('billing.document_issue', { defaultValue: '発行' })}
                </Button>
              ) : null}
            </div>
            <div className="text-muted small mt-2">
              {t('billing.document_action_section_issued', {
                defaultValue: '発行後の操作',
              })}
            </div>
            <div className="d-flex flex-wrap gap-2">
              {current.status === 'issued' ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (onResend && billingDocument) {
                      onResend(billingDocument);
                    }
                  }}
                >
                  {t('billing.document_resend', { defaultValue: '再送' })}
                </Button>
              ) : null}
              {current.status === 'issued' &&
              billingDocument &&
              (billingDocument.type === 'delivery' || billingDocument.type === 'invoice') ? (
                <Button
                  variant="warning"
                  onClick={() => {
                    if (onReissueRequest) {
                      const reason = window.prompt(
                        t('billing.document_reissue_reason', {
                          defaultValue: '再発行理由を入力してください',
                        })
                      );
                      onReissueRequest(billingDocument, reason || undefined);
                    }
                  }}
                >
                  {t('billing.document_reissue_request', { defaultValue: '再発行申請' })}
                </Button>
              ) : null}
              {current.status === 'issued' &&
              billingDocument &&
              billingDocument.type === 'quote' ? (
                <Button
                  variant="warning"
                  onClick={() => {
                    if (onReissueRequest) {
                      onReissueRequest(billingDocument);
                    }
                  }}
                >
                  {t('billing.document_reissue_now', { defaultValue: '再発行' })}
                </Button>
              ) : null}
              {billingDocument && billingDocument.reissueStatus === 'pending' && isAdmin ? (
                <>
                  <Button
                    variant="success"
                    onClick={() => {
                      if (onReissueApprove) {
                        onReissueApprove(billingDocument);
                      }
                    }}
                  >
                    {t('billing.document_reissue_approve', { defaultValue: '再発行承認' })}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => {
                      if (onReissueReject) {
                        const reason = window.prompt(
                          t('billing.document_reissue_reject_reason', {
                            defaultValue: '再発行却下理由を入力してください',
                          })
                        );
                        if (reason) {
                          onReissueReject(billingDocument, reason);
                        }
                      }
                    }}
                  >
                    {t('billing.document_reissue_reject', { defaultValue: '再発行却下' })}
                  </Button>
                </>
              ) : null}
              {current.status === 'issued' && isAdmin ? (
                <Button
                  variant="outline-danger"
                  onClick={() => {
                    if (onVoid && billingDocument) {
                      onVoid(billingDocument);
                    }
                  }}
                >
                  {t('billing.document_void', { defaultValue: '無効化' })}
                </Button>
              ) : null}
              {current.status === 'issued' &&
              billingDocument &&
              billingDocument.type !== 'invoice' ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (onConvert) {
                      onConvert(billingDocument);
                    }
                  }}
                >
                  {t('billing.document_convert', { defaultValue: '次書類を作成' })}
                </Button>
              ) : null}
            </div>
          </>
        )}
        <div className="d-flex flex-wrap gap-2 justify-content-end">
          {current.pdfPath && (
            <Button variant="primary" onClick={handleDownloadPdf} disabled={isDownloading}>
              {isDownloading ? t('billing.invoice_downloading') : t('billing.invoice_download_pdf')}
            </Button>
          )}
          <Button variant="secondary" onClick={onHide}>
            {t('common.close')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
